"use client";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import CalculatorStart from "./forms/calculator-start";
import CalculateShipmentCost from "./forms/calculate-shipment-cost";
import CalculationResults from "./calculation-results";
import type { CalculatorSectionType } from "./types/shipment-calculator.type";
import type {
  ShipmentBasicInfo,
  ShipmentCalculationResult,
} from "./types/shipment-calculator.type";

function CalculatorSection({ data, defaultCountry }: { data: CalculatorSectionType; defaultCountry?: string }) {
  const [step, setStep] = useState(1);
  const stepContentRef = useRef<HTMLDivElement>(null);
  const hasRenderedInitialStep = useRef(false);
  const [shipmentBasicInfo, setShipmentBasicInfo] = useState<ShipmentBasicInfo>({
    method: "",
    kind: "",
    country: defaultCountry === "GH" ? "GH" : "NG",
  });
  const [results, setResults] = useState<ShipmentCalculationResult | null>(null);

  useEffect(() => {
    // initial history state as step 1
    window.history.replaceState({ step: 1 }, "");

    const handlePopState = (event: PopStateEvent) => {
      if (event.state && typeof event.state.step === "number") {
        setStep(event.state.step);
      } else {
        setStep(1);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Custom step updater that pushes state to browser history
  const updateStep = (newStep: React.SetStateAction<number>) => {
    setStep((prevStep) => {
      const nextStep = typeof newStep === "function" ? newStep(prevStep) : newStep;
      if (nextStep > prevStep) {
        window.history.pushState({ step: nextStep }, "");
      }
      return nextStep;
    });
  };

  useEffect(() => {
    if (!hasRenderedInitialStep.current) {
      hasRenderedInitialStep.current = true;
      return;
    }

    const scrollTimeout = window.setTimeout(() => {
      stepContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);

    return () => window.clearTimeout(scrollTimeout);
  }, [step]);

  return (
    <div className="flex items-center justify-center w-full px-4 sm:px-6">
      <div
        id={`shipment-calculator-step-${step}`}
        ref={stepContentRef}
        className={cn(
          "w-full scroll-mt-28 border border-primary-100 bg-[#fbfcff] rounded-2xl px-4 py-8 sm:px-[68px] sm:py-[52px]",
          step === 3 ? "lg:px-[46px] max-w-[1000px] lg:max-w-[1181px] bg-[#B2C2D833]" : "lg:px-[150px] max-w-[824px]",
        )}
      >
        <div key={step} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          {step === 1 && (
            <CalculatorStart 
              setStep={updateStep} 
              setShipmentBasicInfo={setShipmentBasicInfo} 
              defaultCountry={defaultCountry}
            />
          )}
          {step === 2 && (
            <CalculateShipmentCost
              shipmentBasicInfo={shipmentBasicInfo}
              setResults={setResults}
              setStep={updateStep}
              data={data}
            />
          )}
          {step === 3 && <CalculationResults results={results} setStep={updateStep} />}
        </div>
      </div>
    </div>
  );
}

export default CalculatorSection;